import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Download,
  RefreshCw,
} from 'lucide-react';
import { fetchAuditEvents } from '../actions/activityLogActions';
import styles from '../styles/ActivityLog.module.css';

export const CATEGORY_LABELS = {
  player_risk: 'Player Risk',
  user_mgmt: 'User Mgmt',
  balance: 'Balance',
  game: 'Game',
  result: 'Result',
  settings: 'Settings',
  privilege: 'Privilege',
  auth: 'Login',
  security: 'Security',
  system: 'System',
  other: 'Other',
};

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'denied', label: 'Denied' },
];

const ACTION_LABELS = {
  'auth.login': 'Admin panel login',
  'auth.player_login': 'Player login',
  'auth.unlock_login': 'Unlock dashboard login',
  'privilege.denied': 'Permission denied',
  'balance.credit': 'Balance credited',
  'balance.debit': 'Balance debited',
  'result.publish_manual': 'Manual result published',
  'result.auto_generate': 'Auto result generated',
  'result.auto_publish': 'Result published by engine',
  'result.save_manual_preset': 'Manual result preset saved',
  'result.save_game_strategy': 'Game result strategy saved',
  'player.auto_reset_after_win': 'Auto reset after manual win',
  'player.auto_reset_forced_ntp': 'Auto reset of forced NTP',
  'draws.upload': 'Draws uploaded',
  'draws.backup': 'Draws archived',
};

const PAGE_SIZES = [25, 50, 100, 200];
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

export const istDay = (offsetDays = 0) =>
  new Date(Date.now() + IST_OFFSET_MS + offsetDays * 86400000).toISOString().slice(0, 10);

const formatIST = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const words = (s) =>
  String(s || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

export const humanizeAction = (action) => {
  if (!action) return '-';
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const [scope, ...rest] = String(action).split('.');
  if (scope === 'result' && rest[0]?.startsWith('unlock_')) {
    return `Unlock dashboard: ${words(rest[0].slice('unlock_'.length))}`;
  }
  if (!rest.length) return words(scope);
  return `${words(scope)}: ${words(rest.join('.'))}`;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json.length > 200 ? `${json.slice(0, 200)}…` : json;
  }
  return String(value);
};

const targetLabel = (target) => {
  if (!target || (!target.name && !target.id)) return '-';
  const name = target.name || target.id;
  return target.type ? `${words(target.type)} ${name}` : name;
};

const csvCell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows) => {
  const header = ['Time (IST)', 'Category', 'Action', 'Actor', 'Role', 'IP', 'Target Type', 'Target', 'Changes', 'Outcome', 'Error', 'Source'];
  const lines = rows.map((r) => [
    formatIST(r.ts),
    CATEGORY_LABELS[r.category] || r.category,
    humanizeAction(r.action),
    r.actor?.username || '',
    r.actor?.role || '',
    r.actor?.ip || '',
    r.target?.type || '',
    r.target?.name || r.target?.id || '',
    (r.changes || []).map((c) => `${c.field}: ${formatValue(c.old)} -> ${formatValue(c.new)}`).join('; '),
    r.outcome || '',
    r.error || '',
    r.source || '',
  ].map(csvCell).join(','));
  return [header.join(','), ...lines].join('\n');
};

const emptyFilters = (defaultDays) => ({
  from: defaultDays ? istDay(-(defaultDays - 1)) : '',
  to: defaultDays ? istDay(0) : '',
  category: '',
  outcome: '',
  action: '',
  actor: '',
  target: '',
});

/**
 * Audit trail table (who changed what, old → new).
 * categories: restrict to these categories (category filter offers only these).
 * exactTargets: lock the view to one entity (matched by target name or id).
 */
const AuditLogPanel = ({
  categories = null,
  exactTargets = null,
  initialTarget = '',
  defaultDays = 7,
  compact = false,
  exportName = 'audit-log',
  onMeta,
}) => {
  const [draft, setDraft] = useState(() => ({ ...emptyFilters(defaultDays), target: initialTarget || '' }));
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(compact ? 25 : 50);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;

  const categoryKey = categories ? categories.join(',') : '';
  const exactKey = exactTargets ? exactTargets.filter(Boolean).join(',') : '';

  const scopeKey = `${initialTarget}|${defaultDays}|${categoryKey}|${exactKey}`;
  const scopeRef = useRef(scopeKey);
  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    const next = { ...emptyFilters(defaultDays), target: initialTarget || '' };
    setDraft(next);
    setApplied(next);
    setPage(1);
  }, [scopeKey, initialTarget, defaultDays]);

  const requestFilters = useMemo(() => {
    const f = {};
    if (applied.from) f.from = applied.from;
    if (applied.to) f.to = applied.to;
    if (applied.outcome) f.outcome = applied.outcome;
    if (applied.action.trim()) f.action = applied.action.trim();
    if (applied.actor.trim()) f.actor = applied.actor.trim();
    if (exactKey) f.targetExact = exactKey;
    else if (applied.target.trim()) f.target = applied.target.trim();
    if (applied.category) f.category = applied.category;
    else if (categoryKey) f.category = categoryKey;
    return f;
  }, [applied, categoryKey, exactKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAuditEvents({ filters: requestFilters, page, limit })
      .then((res) => {
        if (cancelled) return;
        setData({ rows: Array.isArray(res.rows) ? res.rows : [], total: Number(res.total) || 0 });
        setExpanded({});
        if (onMetaRef.current) onMetaRef.current({ restrictedAllowed: Boolean(res.restrictedAllowed) });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load audit log');
        setData({ rows: [], total: 0 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestFilters, page, limit, reloadKey]);

  const totalPages = Math.max(Math.ceil(data.total / limit), 1);

  const setField = (key) => (e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  const applyFilters = (e) => {
    e.preventDefault();
    setApplied({ ...draft });
    setPage(1);
  };

  const resetFilters = () => {
    const next = { ...emptyFilters(defaultDays), target: initialTarget || '' };
    setDraft(next);
    setApplied(next);
    setPage(1);
  };

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await fetchAuditEvents({ filters: requestFilters, exportAll: true });
      const blob = new Blob([toCsv(res.rows || [])], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportName}-${istDay(0)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [requestFilters, exportName]);

  const categoryOptions = categories && categories.length > 1 ? categories : null;

  return (
    <div className={styles.auditPanel}>
      <form className={styles.auditFilters} onSubmit={applyFilters}>
        <label className={styles.filterField}>
          <span>From</span>
          <input type="date" value={draft.from} max={draft.to || undefined} onChange={setField('from')} />
        </label>
        <label className={styles.filterField}>
          <span>To</span>
          <input type="date" value={draft.to} min={draft.from || undefined} onChange={setField('to')} />
        </label>
        {categoryOptions && (
          <label className={styles.filterField}>
            <span>Category</span>
            <select value={draft.category} onChange={setField('category')}>
              <option value="">All</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
              ))}
            </select>
          </label>
        )}
        <label className={styles.filterField}>
          <span>Outcome</span>
          <select value={draft.outcome} onChange={setField('outcome')}>
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className={styles.filterField}>
          <span>Who (actor)</span>
          <input type="text" placeholder="username" value={draft.actor} onChange={setField('actor')} />
        </label>
        {!exactKey && (
          <label className={styles.filterField}>
            <span>Target</span>
            <input type="text" placeholder="username / id" value={draft.target} onChange={setField('target')} />
          </label>
        )}
        <label className={styles.filterField}>
          <span>Action</span>
          <input type="text" placeholder="e.g. allowed, publish" value={draft.action} onChange={setField('action')} />
        </label>
        <div className={styles.filterActions}>
          <button type="submit" className={styles.primaryButton}>Apply</button>
          <button type="button" className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setReloadKey((k) => k + 1)}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleExport}
            disabled={exporting || data.total === 0}
            title="Export CSV (up to 5000 rows)"
          >
            {exporting ? <Loader2 size={14} className={styles.spinner} /> : <Download size={14} />}
            <span>CSV</span>
          </button>
        </div>
      </form>

      {error && (
        <div className={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={`${styles.table} ${styles.auditTable}`}>
          <thead>
            <tr>
              <th aria-label="Expand" />
              <th>Time (IST)</th>
              <th>Who</th>
              <th>Action</th>
              <th>Target</th>
              <th>Change (old → new)</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className={styles.emptyState}>
                  <Loader2 className={styles.spinner} size={28} />
                </td>
              </tr>
            ) : data.rows.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyState}>No audit events found.</td>
              </tr>
            ) : (
              data.rows.map((row) => {
                const id = row._id;
                const isOpen = Boolean(expanded[id]);
                const changes = row.changes || [];
                return (
                  <React.Fragment key={id}>
                    <tr className={styles.auditRow} onClick={() => toggleRow(id)}>
                      <td>
                        <ChevronDown size={16} className={isOpen ? styles.chevronOpen : styles.chevron} />
                      </td>
                      <td className={styles.nowrap}>{formatIST(row.ts)}</td>
                      <td>
                        <div className={styles.actorName}>{row.actor?.username || '-'}</div>
                        <div className={styles.muted}>
                          {[row.actor?.role, row.actor?.ip].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.categoryBadge} ${styles[`cat_${row.category}`] || ''}`}>
                          {CATEGORY_LABELS[row.category] || row.category}
                        </span>
                        <div>{humanizeAction(row.action)}</div>
                      </td>
                      <td>{targetLabel(row.target)}</td>
                      <td className={styles.changesCell}>
                        {changes.length === 0 ? (
                          <span className={styles.muted}>—</span>
                        ) : (
                          changes.slice(0, 4).map((c) => (
                            <div key={c.field} className={styles.changeLine}>
                              <span className={styles.changeField}>{c.field}</span>
                              <span className={styles.oldValue}>{formatValue(c.old)}</span>
                              <span className={styles.arrow}>→</span>
                              <span className={styles.newValue}>{formatValue(c.new)}</span>
                            </div>
                          ))
                        )}
                        {changes.length > 4 && (
                          <div className={styles.muted}>+{changes.length - 4} more</div>
                        )}
                      </td>
                      <td>
                        <span className={`${styles.outcomeBadge} ${styles[`outcome_${row.outcome}`] || ''}`}>
                          {row.outcome || '-'}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className={styles.detailRow}>
                        <td colSpan={7}>
                          <div className={styles.detailGrid}>
                            <div>
                              <div className={styles.detailLabel}>Action key</div>
                              <code>{row.action}</code>
                            </div>
                            <div>
                              <div className={styles.detailLabel}>Source</div>
                              <span>{row.source || '-'}</span>
                            </div>
                            {row.actor?.user_agent && (
                              <div>
                                <div className={styles.detailLabel}>User agent</div>
                                <span className={styles.breakAll}>{row.actor.user_agent}</span>
                              </div>
                            )}
                            {row.error && (
                              <div>
                                <div className={styles.detailLabel}>Error</div>
                                <span className={styles.errorText}>{row.error}</span>
                              </div>
                            )}
                            {row.request_id && (
                              <div>
                                <div className={styles.detailLabel}>Request id</div>
                                <code>{row.request_id}</code>
                              </div>
                            )}
                          </div>
                          {changes.length > 0 && (
                            <table className={styles.changesTable}>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Old</th>
                                  <th>New</th>
                                </tr>
                              </thead>
                              <tbody>
                                {changes.map((c) => (
                                  <tr key={c.field}>
                                    <td>{c.field}</td>
                                    <td className={styles.oldValue}>{formatValue(c.old)}</td>
                                    <td className={styles.newValue}>{formatValue(c.new)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {row.context && Object.keys(row.context).length > 0 && (
                            <>
                              <div className={styles.detailLabel}>Context</div>
                              <pre className={styles.contextPre}>{JSON.stringify(row.context, null, 2)}</pre>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.paginationContainer}>
        <div className={styles.paginationLeft}>
          <button
            type="button"
            className={styles.paginationButton}
            onClick={() => setPage(1)}
            disabled={page <= 1 || loading}
            title="First Page"
            aria-label="First Page"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            type="button"
            className={styles.paginationButton}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page <= 1 || loading}
            title="Previous Page"
            aria-label="Previous Page"
          >
            <ChevronLeft size={16} />
          </button>
        </div>
        <div className={styles.paginationCenter}>
          <span className={styles.paginationText}>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> · {data.total} events
          </span>
          <div className={styles.pageSizeSelector}>
            <label htmlFor="auditPageSize" className={styles.paginationText}>Show:</label>
            <select
              id="auditPageSize"
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
              className={styles.pageSizeSelect}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
        <div className={styles.paginationRight}>
          <button
            type="button"
            className={styles.paginationButton}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page >= totalPages || loading}
            title="Next Page"
            aria-label="Next Page"
          >
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            className={styles.paginationButton}
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages || loading}
            title="Last Page"
            aria-label="Last Page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPanel;
